import { useTranslation } from 'react-i18next';
import styled, { css } from 'styled-components';

import { SportType } from '__generated__/graphql';

import { assetUrl } from '../../common/helpers';
import UI from '../../common/UI';

interface AvatarProps {
  size?: number;
  background?: string;
  sportType?: SportType;
}

export const Avatar = ({ sportType = SportType.running, size, background }:AvatarProps) => {
  const { t } = useTranslation();

  const images: Partial<Record<SportType, string>> = {
    [SportType.running]: assetUrl('images/avatars/running.svg'),
    [SportType.cycling]: assetUrl('images/avatars/cycling.svg'),
    [SportType.walking]: assetUrl('images/avatars/walking.svg'),
    [SportType.fitness]: assetUrl('images/avatars/fitness.svg'),
  };

  const imageSrc = images[sportType] || assetUrl('images/avatars/default.svg');

  return (
    <AvatarBorder size={size}>
      <AvatarContainer background={background}>
        <svg
          id="background"
          xmlns="http://www.w3.org/2000/svg"
          width="160"
          height="160"
          xmlnsXlink="http://www.w3.org/1999/xlink"
          viewBox="0 0 160 160"
        >
          <defs>
            <radialGradient
              id="Naamloos_verloop_958"
              data-name="Naamloos verloop 958"
              cx="80"
              cy="80"
              fx="80"
              fy="80"
              r="80"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#fff" />
              <stop offset="1" stopColor="#b3dfee" />
            </radialGradient>
            <linearGradient
              id="Naamloos_verloop_1023"
              data-name="Naamloos verloop 1023"
              x1="80"
              y1="89.79"
              x2="80"
              y2="44.89"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#7acbea" />
              <stop offset="1" stopColor="#a0d7ec" stopOpacity="0" />
            </linearGradient>
            <linearGradient
              id="Naamloos_verloop_1004"
              data-name="Naamloos verloop 1004"
              x1="0"
              y1="124.89"
              x2="160"
              y2="124.89"
              gradientUnits="userSpaceOnUse"
            >
              <stop offset="0" stopColor="#fff" />
              <stop offset="1" stopColor="#b3dfee" />
            </linearGradient>
          </defs>
          <rect width="160" height="160" fill="url(#Naamloos_verloop_958)" />
          <rect y="44.89" width="160" height="44.89" fill="url(#Naamloos_verloop_1023)" />
          <rect y="89.79" width="160" height="70.21" fill="url(#Naamloos_verloop_1004)" />
        </svg>
        <img src={imageSrc} alt={t('website.avatar')} />
      </AvatarContainer>
    </AvatarBorder>
  );
};

const AvatarContainer = styled(UI.Div)<{ background?: string; }>`
  ${({ theme, background = 'white' }) => css`
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    background: white;
    background: #b3dfee;
    border: 3px solid ${theme.getColor(background)};
    overflow: hidden;
    position: relative;
    color: ${theme.colors.tertiary[700]};

    svg {
      display: block;
      position: relative;
      width: 100%;
      height: 100%;
      z-index: 1;
      animation: avatar-bg-fade-in 0.25s ease-in;

      @keyframes avatar-bg-fade-in {
        0% {
          opacity: 0;
        }

        100% {
          opacity: 1;
        }
      }
    }

    img {
      width: 100%;
      height: 100%;
      display: block;
      position: absolute;
      z-index: 2;
      animation: avatar-icon-slide-in 0.25s ease-in-out;

      @keyframes avatar-icon-slide-in {
        0% {
          transform: translateY(10px);
          opacity: 0;
        }

        30% {
          transform: translateY(10px);
          opacity: 0;
        }

        100% {
          transform: translateY(0);
          opacity: 1;
        }
      }
    }
  `}
`;

const AvatarBorder = styled(UI.Div)<{ size?: number; }>`
  ${({ size = 80, theme }) => css`
    display: flex;
    background: linear-gradient(-45deg, ${theme.colors.primary[500]} 0%, ${theme.colors.tertiary[300]} 50%, ${theme.colors.secondary[500]} 100%);
    border-radius: 30%;
    padding: 4px;
    width: ${size}px;
    height: ${size}px;

    ${AvatarContainer} {
      box-shadow: 0 5px 25px ${theme.transparentize(theme.colors.tertiary[300], 0.5)}, inset 0 0 10px ${theme.colors.tertiary[200]};
      transition: box-shadow 0.15s ease-in-out;
      border-radius: ${30 * ((size - 4) / size)}%;
    }

    &:hover {
      ${AvatarContainer} {
        box-shadow: 0 0px 45px ${theme.transparentize(theme.colors.tertiary[300], 0.5)}, inset 0 0 10px ${theme.colors.tertiary[300]};
      }
    }
  `}
`;

export default undefined;
